import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { mxSharedApi, getResourceTypeList, getResourceSubtypeList, getUtilityServices, getMeterReadMethods, putMeterReadMethod, getMxChoices, getMeterManufacturers, getMeterModels, getWarehouses, getOems, getMeterReasonCodesList, getMeterReadingStatusList } from "./api";
import { queryClient } from "@shared/api/queries/queryClient";
import { useToast } from "@shared/hooks/use-toast";

export const useGetNotes = (id: string, type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.shared.notesList(id, type),
    () => mxSharedApi.getNotes(id, type)
  );
};

export const useCreateNote = () => {
      const { toast } = useToast();
  return useSmartMutation((payload: any) => mxSharedApi.createNote(payload), {
    onSuccess: () => {
      toast({
        title: "Note created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.mx.shared.notesList().slice(0, 4);

      queryClient.invalidateQueries({
        queryKey: key,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create a note: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useGetActivityLog = (id: string, type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.shared.activityLogList(id, type),
    () => mxSharedApi.getActivityLog(id, type)
  );
};

export const useGetResourceTypeList = (remoteUtilityId: string, page?: number, limit?: number, searchData?: string) => {
  return useSmartQuery(
    ["mx", "shared", "resource-type-list", remoteUtilityId, page, limit, searchData],
    () => getResourceTypeList(remoteUtilityId, page, limit, searchData),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useGetResourceSubtypeList = (remoteUtilityId: string, codes?: string[], page?: number, limit?: number, searchData?: string) => {
  return useSmartQuery(
    ["mx", "shared", "resource-subtype-list", remoteUtilityId, codes, page, limit, searchData],
    () => getResourceSubtypeList(remoteUtilityId, codes, page, limit, searchData),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useGetUtilityServices = (utilityId: string) => {
  return useSmartQuery(
    ["mx", "shared", "utility-services", utilityId],
    () => getUtilityServices(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetMeterReadMethods = (remoteUtilityId: string) => {
  return useSmartQuery(
    ["mx", "shared", "meter-read-methods", remoteUtilityId],
    () => getMeterReadMethods(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const usePutMeterReadMethod = () => {
  const { toast } = useToast();
  return useSmartMutation(
    ({ id, payload }: { id: number; payload: { is_active: boolean } }) =>
      putMeterReadMethod(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Meter read method updated successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "shared", "meter-read-methods"],
        });
      },
    }
  );
};

export const useChoices = ({
  choice,
  remoteUtilityId,
}: {
  choice: string;
  remoteUtilityId?: string;
}) => {
  return useSmartQuery(QueryKeyFactory.module.mx.choices(choice), () =>
    getMxChoices(choice, remoteUtilityId),
  );
};

export const useMeterManufacturers = (params: {
  remote_utility_id: string;
  is_pagination_required?: boolean;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.shared.meterManufacturers(params),
    () => getMeterManufacturers(params)
  );
};

export const useMeterModels = (
  remoteUtilityId: string,
  manufacturer?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModels({
      remote_utility_id: remoteUtilityId,
      manufacturer,
    }),
    () => getMeterModels(remoteUtilityId, manufacturer),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useWarehouses = (
  remoteUtilityId: string,
) =>
  useSmartQuery(
    ["mx", "warehouse-list", remoteUtilityId],
    () => getWarehouses(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );

export const useOems = (
  remoteUtilityId: string,
) =>
  useSmartQuery(
    ["mx", "oem-list", remoteUtilityId],
    () => getOems(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );

export const useMeterReasonCodesList = (
  remoteUtilityId: string,
  page?: number,
  limit?: number,
  search?: string,
  meterReadingStatus?: string,
) => {
  return useSmartQuery(
    ["mx", "shared", "reason-code-list", remoteUtilityId, page, limit, search, meterReadingStatus],
    () =>
      getMeterReasonCodesList(
        remoteUtilityId,
        page,
        limit,
        search,
        meterReadingStatus,
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useMeterReadingStatusList = (
  remoteUtilityId: string,
  page?: number,
  limit?: number,
  search?: string,
) => {
  return useSmartQuery(
    ["mx", "shared", "meter-reading-status-list", remoteUtilityId, page, limit, search],
    () => getMeterReadingStatusList(remoteUtilityId, page, limit, search),
    { enabled: !!remoteUtilityId },
  );
};
